"use client";

import Button from "@/components/ui/Button";
import { useAutoTranslate } from "@/lib/i18n/useAutoTranslate";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslations } from "next-intl";
import * as React from "react";
import Skeleton from "react-loading-skeleton";

// Generic type for table data
export type TableData = Record<string, any>;

// Column definition type
export type ColumnDef<TData extends TableData> = {
  accessorKey: keyof TData;
  header: string;
  cell?: (info: {
    getValue: () => any;
    row: any;
    column: any;
  }) => React.ReactNode;
  sortable?: boolean;
};

// Pagination props
export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

// Main table props
export interface DataTableProps<TData extends TableData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  pagination?: PaginationProps;
  searchable?: boolean;
  onSearch?: (value: string) => void;
  searchValue?: string;
  searchPlaceholder?: string;
  loading?: boolean;
  error?: any;
  emptyMessage?: string;
  className?: string;
}

const columnHelper = createColumnHelper<TableData>();

export default function DataTable<TData extends TableData>({
  data,
  columns,
  pagination,
  searchable = false,
  onSearch,
  searchValue = "",
  searchPlaceholder = "Search...",
  loading = false,
  error,
  emptyMessage,
  className = "",
}: DataTableProps<TData>) {
  const t = useTranslations("datatable");
  const tr = useAutoTranslate();
  const [globalFilter, setGlobalFilter] = React.useState(searchValue);
  const rerender = React.useReducer(() => ({}), {})[1];
  const effectiveSearchPlaceholder = tr(searchPlaceholder || t("searchPlaceholder"));
  const effectiveEmptyMessage = tr(emptyMessage || t("noData"));

  // Update global filter when searchValue prop changes
  React.useEffect(() => {
    setGlobalFilter(searchValue);
  }, [searchValue]);

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setGlobalFilter(value);
    onSearch?.(value);
  };

  // Convert columns to TanStack Table format
  const tableColumns = React.useMemo(() => {
    return columns.map((col) => {
      const column = columnHelper.accessor(col.accessorKey as string, {
        header: col.header,
        cell: col.cell ? (info) => col.cell!(info) : (info) => info.getValue(),
      });

      return column;
    });
  }, [columns]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (error) {
    return (
      <div className={`p-4 text-red-500 ${className}`}>{tr(t("failedLoad"))}</div>
    );
  }

  return (
    <div className={`p-4 ${className}`}>
      {/* Search */}
      {searchable && (
        <div className="mb-4 flex items-center gap-2">
          <input
            value={globalFilter ?? ""}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={effectiveSearchPlaceholder}
            className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />
          <Button
            onClick={() => rerender()}
            className="rounded bg-blue-600 px-3 !py-2 text-sm"
          >
            Search
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-bright-gray ">
        <table className="min-w-full divide-y divide-gray-200 text-sm text-left text-gray-700">
          <thead className="bg-gray-100 text-sm uppercase text-gray-500">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`!px-4 !py-3 select-none ${
                      columns.find(
                        (col) => col.accessorKey === header.column.id
                      )?.sortable !== false
                        ? "cursor-pointer"
                        : ""
                    }`}
                    onClick={
                      columns.find(
                        (col) => col.accessorKey === header.column.id
                      )?.sortable !== false
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    <div className="flex items-center justify-between">
                      <span>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </span>
                      {columns.find(
                        (col) => col.accessorKey === header.column.id
                      )?.sortable !== false && (
                        <div className="ml-2 w-4 h-4 flex items-center justify-center">
                          {header.column.getIsSorted() === "asc" ? (
                            <CaretUpOutlined className="text-blue-600 text-sm" />
                          ) : header.column.getIsSorted() === "desc" ? (
                            <CaretDownOutlined className="text-blue-600 text-sm" />
                          ) : (
                            <div className="w-4 h-4" /> // Invisible placeholder to maintain width
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white ">
            {loading ? (
              // Show skeleton rows for loading state
              Array.from({ length: pagination?.pageSize || 10 }).map(
                (_, index) => (
                  <tr key={`skeleton-${index}`} className="hover:bg-gray-50">
                    {Array.from({ length: columns.length }).map(
                      (_, cellIndex) => (
                        <td
                          key={`skeleton-cell-${index}-${cellIndex}`}
                          className="px-4 py-3"
                        >
                          <Skeleton height={15} />
                        </td>
                      )
                    )}
                  </tr>
                )
              )
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  {effectiveEmptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination && !loading && (
        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="text-sm text-gray-500 ml-4">
            {t("showing", {
              start: pagination.currentPage * pagination.pageSize + 1,
              end: Math.min(
                (pagination.currentPage + 1) * pagination.pageSize,
                pagination.totalCount
              ),
              total: pagination.totalCount,
            })}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              onClick={() =>
                pagination.onPageChange(pagination.currentPage - 1)
              }
              disabled={pagination.currentPage === 0 || loading}
              className="px-2 text-sm border !py-2  cursor-pointer "
            >
              Previous
            </Button>

            {/* Show page numbers with ellipsis for large page counts */}
            {(() => {
              const pages = [];
              const maxVisiblePages = 5;

              if (pagination.totalPages <= maxVisiblePages) {
                // Show all pages if total is small
                for (let i = 0; i < pagination.totalPages; i++) {
                  pages.push(i);
                }
              } else {
                // Show first page, last page, and pages around current
                // Always show first page (0) if we're not already showing it
                if (pagination.currentPage > 1) {
                  pages.push(0);
                  if (pagination.currentPage > 3) pages.push("...");
                }

                const start = Math.max(0, pagination.currentPage - 1);
                const end = Math.min(
                  pagination.totalPages - 1,
                  pagination.currentPage + 1
                );

                for (let i = start; i <= end; i++) {
                  pages.push(i);
                }

                if (pagination.currentPage < pagination.totalPages - 2)
                  pages.push("...");
                if (pagination.currentPage < pagination.totalPages - 1)
                  pages.push(pagination.totalPages - 1);
              }

              return pages.map((page, index) => (
                <React.Fragment key={index}>
                  {page === "..." ? (
                    <span className="px-2 py-1 text-gray-400 text-sm">...</span>
                  ) : (
                    <button
                      onClick={() => pagination.onPageChange(page as number)}
                      disabled={loading}
                      className={`px-2 py-1 text-sm rounded min-w-[32px] cursor-pointer ${
                        (page as number) === pagination.currentPage
                          ? "bg-blue-50 text-blue-600 font-medium"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {(page as number) + 1}
                    </button>
                  )}
                </React.Fragment>
              ));
            })()}

            <Button
              onClick={() =>
                pagination.onPageChange(pagination.currentPage + 1)
              }
              disabled={
                pagination.currentPage >= pagination.totalPages - 1 || loading
              }
              className="px-2 text-sm bg-gray-100 !py-2"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
